import { POPUP_GAPSPACE } from '../utils/constants';
import getDirectionalHint from '../utils/getDirectionalHint';
import { observer } from 'mobx-react-lite';
import { ActionButton, PrimaryButton } from 'office-ui-fabric-react/lib/Button';
import { Callout } from 'office-ui-fabric-react/lib/Callout';
import { ITextField, TextField } from 'office-ui-fabric-react/lib/components/TextField';
import { IPoint } from 'office-ui-fabric-react/lib/Utilities';
import loc from 'owa-localize';
import { openFlexPane } from 'owa-suite-header-apis';
import { trace } from 'owa-trace';
import { addUserDataItemToVaultLazy } from 'owa-vault';
import { createUserDataInfoService, UserDataStoreRequestStatus } from 'owa-userdatastore-service';
import React from 'react';
import * as ReactDOM from 'react-dom';
import {
    Vault_header,
    Vault_instruction,
    Vault_keyConflict,
    Vault_keyIsRequired,
    Vault_manage,
    Vault_save,
} from './VaultPopup.locstring.json';
const styles = require('./VaultPopup.scss');
// The maximum length of the selected text that can be
// displayed in one line in the pop-up
const POP_UP_LENGTH_LIMIT = 30;
const USER_INFO_CATEGORY = 'UserInfo';

interface VaultPopupProps {
    target: IPoint | HTMLElement;
    selectedText: string;
}

let vaultDiv: HTMLDivElement = null;

// The pop-up panel for the save to Vault option.
// It will take in the user selected text and input name field as a key-value pair.
const VaultPopup = observer(function VaultPopup(props: VaultPopupProps) {
    const { target, selectedText } = props;

    const textField = React.useRef<ITextField>();
    const [errorMessage, setErrorMessage] = React.useState('');
    const onSave = async () => {
        const key = textField.current ? textField.current.value : '';
        if (key.length === 0) {
            setErrorMessage(Vault_keyIsRequired);
            return;
        }

        const status = await createUserDataInfoService(key, selectedText, USER_INFO_CATEGORY);

        switch (status) {
            case UserDataStoreRequestStatus.SUCCESS:
                setErrorMessage('');
                addUserDataItemToVaultLazy.importAndExecute(USER_INFO_CATEGORY, key, selectedText);
                destroyPopupDiv();
                break;
            case UserDataStoreRequestStatus.CONFLICT:
                setErrorMessage(Vault_keyConflict);
                break;
            default:
                trace.info(`Invalid UserDataStore request status: [${status}].`);
                break;
        }
    };

    const manageVault = () => {
        openFlexPane('OwaVaultPanel');
    };

    return (
        <Callout
            gapSpace={POPUP_GAPSPACE}
            className={styles.vaultPopupCalloutStyles}
            isBeakVisible={false}
            directionalHint={getDirectionalHint(selectedText)}
            target={target}
            onDismiss={destroyPopupDiv}>
            <div className={styles.headerContainer}>
                <div className={styles.vaultHeaderStyle}>{loc(Vault_header)}</div>
                <ActionButton
                    className={styles.vaultButtonStyles}
                    iconProps={{
                        iconName: 'Cancel',
                        styles: { root: styles.vaultButtonIconStyles },
                    }}
                    onClick={destroyPopupDiv}
                />
            </div>
            <div className={styles.contentContainer}>
                <TextField
                    componentRef={textField}
                    className={styles.vaultTextField}
                    autoFocus={true}
                />
                <TextField
                    className={styles.vaultTextField}
                    disabled={true}
                    multiline={selectedText.length > POP_UP_LENGTH_LIMIT}
                    resizable={false}
                    value={selectedText}
                />
                <div className={styles.vaultInstructionStyle}>{loc(Vault_instruction)}</div>
            </div>
            <div className={styles.footerContainer}>
                <ActionButton
                    className={styles.manageVaultButtonStyles}
                    onClick={manageVault}
                    text={loc(Vault_manage)}
                />
                <PrimaryButton
                    text={loc(Vault_save)}
                    className={styles.saveButtonStyles}
                    onClick={onSave}
                />
            </div>
            {errorMessage?.length > 0 && (
                <span className={styles.errorLabel}>{loc(errorMessage)}</span>
            )}
        </Callout>
    );
});

function createVaultPopupDiv() {
    vaultDiv = document.createElement('div');
    document.body.appendChild(vaultDiv);
}

function destroyPopupDiv() {
    if (vaultDiv) {
        ReactDOM.unmountComponentAtNode(vaultDiv);
        vaultDiv.parentNode.removeChild(vaultDiv);
        vaultDiv = null;
    }
}

export default function showVaultPopup(target: IPoint | HTMLElement, selectedText: string) {
    if (!vaultDiv) {
        createVaultPopupDiv();
    }

    ReactDOM.render(
        <React.StrictMode>
            <VaultPopup target={target} selectedText={selectedText} />
        </React.StrictMode>,
        vaultDiv
    );
}
